/**
 * Service-form UX: let users tap Continue anytime; if required input is empty,
 * scroll + focus that control instead of only greying the button / listing “still need”.
 *
 * Use with data attributes, e.g. data-partner-field="phone", data-water-field="drop_pin".
 */
import { scrollFieldIntoVisibleViewport } from './scrollFieldIntoVisibleViewport';

export type FocusFormFieldOptions = {
  /**
   * Attribute name without the `data-` prefix.
   * `partner-field` → `[data-partner-field="…"]`
   */
  attr?: string;
  /** Delay after rAF so step/layout can settle (default 80ms). */
  delayMs?: number;
  container?: ParentNode | Document | null;
  /** Draw a temporary ring around the control so the gap is obvious. Default true. */
  highlight?: boolean;
  /** How long the ring stays (ms). Default 4500. */
  highlightMs?: number;
  /** Service accent for the ring. Default sky. */
  color?: string;
};

const HIGHLIGHT_STYLE_ID = 'zia-form-field-highlight-style';
const HIGHLIGHT_ATTR = 'data-zia-field-highlight';

function safeFieldId(fieldId: string): string {
  return String(fieldId || '').replace(/["\\]/g, '');
}

function highlightHex(color?: string): string {
  const raw = String(color || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  return '#38bdf8';
}

function highlightRgba(hex: string, alpha: number): string {
  const n = hex.slice(1);
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function ensureHighlightStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(HIGHLIGHT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HIGHLIGHT_STYLE_ID;
  style.textContent = `
    [${HIGHLIGHT_ATTR}="1"] {
      border-radius: 10px !important;
      box-shadow: 0 0 0 2px var(--zia-field-highlight, #38bdf8), 0 0 0 5px var(--zia-field-highlight-glow, rgba(56, 189, 248, 0.32)) !important;
      transition: box-shadow 0.2s ease !important;
      outline: none !important;
    }
    [${HIGHLIGHT_ATTR}="1"] .MuiOutlinedInput-notchedOutline {
      border-color: var(--zia-field-highlight, #38bdf8) !important;
      border-width: 2px !important;
    }
    [${HIGHLIGHT_ATTR}="1"] .MuiInputBase-root {
      border-color: var(--zia-field-highlight, #38bdf8) !important;
    }
    [${HIGHLIGHT_ATTR}="1"] .MuiRadio-root,
    [${HIGHLIGHT_ATTR}="1"] .MuiCheckbox-root {
      color: var(--zia-field-highlight, #38bdf8) !important;
    }
  `;
  document.head.appendChild(style);
}

/** Persistent unanswered-field ring in the service accent (not generic red). */
export function missingFieldRingSx(accent: string): Record<string, string | number> {
  const color = highlightHex(accent);
  return {
    outline: `2px solid ${color}`,
    outlineOffset: 2,
    borderRadius: 10,
  };
}

/** Remove any previous rings in the document (or a container). */
export function clearFormFieldHighlights(container?: ParentNode | Document | null): void {
  if (typeof document === 'undefined') return;
  const root: ParentNode | Document = container || document;
  root.querySelectorAll(`[${HIGHLIGHT_ATTR}]`).forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.removeProperty('--zia-field-highlight');
      el.style.removeProperty('--zia-field-highlight-glow');
    }
    el.removeAttribute(HIGHLIGHT_ATTR);
  });
}

/**
 * Scroll the marked control into view and focus the first interactive child (or itself).
 * Optionally rings the control so users see exactly what blocked Continue / Pay.
 * Returns true if a node was found (focus is scheduled async).
 */
export function focusFormField(fieldId: string, options?: FocusFormFieldOptions): boolean {
  if (typeof document === 'undefined') return false;
  const attr = String(options?.attr || 'form-field').trim() || 'form-field';
  const id = safeFieldId(fieldId);
  if (!id) return false;
  const root: ParentNode | Document = options?.container || document;
  const delayMs = options?.delayMs != null ? Math.max(0, options.delayMs) : 80;
  const highlight = options?.highlight !== false;
  const highlightMs = options?.highlightMs != null ? Math.max(500, options.highlightMs) : 4500;

  const selector = `[data-${attr}="${id}"]`;
  const found = root.querySelector<HTMLElement>(selector);
  if (!found) return false;

  const apply = () => {
    const node = root.querySelector<HTMLElement>(selector);
    if (!node) return;
    if (!scrollFieldIntoVisibleViewport(node) && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const focusTarget = node.matches(
      'input, textarea, select, button, [role="combobox"], [role="radio"], [role="checkbox"], [tabindex]:not([tabindex="-1"])',
    )
      ? node
      : node.querySelector<HTMLElement>(
          'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="combobox"], [role="radio"], [role="checkbox"], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );

    if (highlight) {
      ensureHighlightStyles();
      clearFormFieldHighlights(root);
      const color = highlightHex(options?.color);
      // Prefer highlighting the field chrome (TextField / radio group) when present
      const ringTarget =
        (focusTarget?.closest(
          '.MuiFormControl-root, .MuiTextField-root, .MuiButton-root, .MuiFormGroup-root, .MuiRadioGroup-root, [role="radiogroup"]',
        ) as HTMLElement | null)
        || node;
      ringTarget.style.setProperty('--zia-field-highlight', color);
      ringTarget.style.setProperty('--zia-field-highlight-glow', highlightRgba(color, 0.32));
      ringTarget.setAttribute(HIGHLIGHT_ATTR, '1');
      window.setTimeout(() => {
        ringTarget.style.removeProperty('--zia-field-highlight');
        ringTarget.style.removeProperty('--zia-field-highlight-glow');
        ringTarget.removeAttribute(HIGHLIGHT_ATTR);
      }, highlightMs);
    }

    try {
      focusTarget?.focus({ preventScroll: true });
    } catch {
      focusTarget?.focus();
    }
  };

  requestAnimationFrame(() => {
    window.setTimeout(apply, delayMs);
  });
  return true;
}

/**
 * Prefer shared helper for service hubs that already used per-product wrappers.
 * Same as focusFormField with a fixed attr.
 */
export function makeFieldFocuser(
  attr: string,
  getContainer?: () => ParentNode | Document | null,
  color?: string,
) {
  return (fieldId: string, delayMs?: number) =>
    focusFormField(fieldId, {
      attr,
      delayMs,
      container: getContainer?.() ?? document,
      highlight: true,
      color,
    });
}

/**
 * Standard “blocked Continue” pattern for delivery hubs:
 * set message, scroll/focus/ring the control, optional haptic via caller.
 */
export function jumpToMissingField(
  fieldId: string,
  options?: FocusFormFieldOptions,
): boolean {
  return focusFormField(fieldId, { highlight: true, delayMs: 100, ...options });
}
